import moment from "moment";
import { useContext, useEffect, useState } from "react";
import config from "config/config";
import { Grid } from "gridjs-react";
import { v4 as uuidv4 } from "uuid";
import * as fileSaver from "file-saver";
import { useDispatch } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import { getImage } from "redux/slices/getImageSlice";
import { useCollapsePanel } from "hooks/useCollapsePanel";
import { ChatContext } from "context/Agent/ChatProvider";
import { getFinancials } from "service/policy/getFinancials";
import {
  base64ToArrayBuffer,
  convertToTitleCase,
  getBadgeText,
  getCompanyNameByBlockCode,
  stringFormattingToCurrency,
  filterAndFormatRiderPolicies,
  getBenefitName,
} from "js/formatting";
import {
  isFlagEnabled,
  isMITPolicy,
  isOldAlicUlPolicy,
  isPolicyInGrace,
  isWholeLifePolicy,
  showCoverageAmountForBenefit,
} from "js/utilities";

import Badge from "components/General/Badge";
import PDFDialog from "components/ModalDialogs/PDFDialog";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import CardDisclaimerBase from "components/General/CardDisclaimerBase";

const CoverageAndDocuments = ({ policy, filteredDocuments }) => {
  const { t } = useTranslation();
  const { openChatWindow, setOpenChatWindow } = useContext(ChatContext);

  let dispatch = useDispatch();
  const uuid = uuidv4();
  useCollapsePanel(uuid);
  const [loading, setLoading] = useState(false);
  const [pdfSource, setPdfSource] = useState("");
  const [desc, setDesc] = useState("");
  const [pdfDialogState, setPdfDialogState] = useState(false);
  const [name, setName] = useState("");
  const [lapsedOn, setLapsedOn] = useState();

  useEffect(() => {
    if (policy?.status?.toLowerCase() === "lapsed") {
      setLapsedOn(moment(policy?.statusDate).format("MM/DD/YYYY"));
    } else if (
      !isOldAlicUlPolicy(policy) &&
      isPolicyInGrace(policy) &&
      policy.status?.toLowerCase() !== "extended term"
    ) {
      getFinancials(policy)
        .then((response) => {
          // Sort in descending order
          let cashValues = response.cashValues?.sort(
            (a, b) =>
              moment(b?.projectedlapseDate) - moment(a?.projectedlapseDate)
          );
          const lapseDate = cashValues[0]?.projectedlapseDate;
          setLapsedOn(lapseDate);
          setLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
        });
    }
  }, [policy]);

  const renderLapseDate = (policy) => {
    if (lapsedOn) {
      return (
        <div>
          <div className="text-xs text-theme-text-tertiary">
            {policy?.status?.toLowerCase() === "lapsed"
              ? t("AuthAgentPolicyDetail-lapsed-date-label")
              : "Lapse Date"}
          </div>
          <div className="text-sm font-semibold text-theme-text-secondary">
            {lapsedOn}
          </div>
        </div>
      );
    }
    return null;
  };

  const additionalBenefitsGrid = (policy) => {
    return (
      <Grid
        columns={[
          {
            name: t("AuthAgentPolicyDetail-additional-benefits-label-1"),
            data: (row) => (row?.isRider ? row.type : getBenefitName(row.type)),
          },
          {
            name: t("AuthAgentPolicyDetail-coverage-documents-label-8"),
            data: (row) =>
              row?.isRider
                ? stringFormattingToCurrency(row.benefit)
                : !showCoverageAmountForBenefit(row.type)
                ? stringFormattingToCurrency(row.benefit)
                : "",
          },
          ...(isFlagEnabled("SHOW_ISSUED_DATE_IN_ADDITIONAL_BENEFITS_SECTION")
            ? [
                {
                  name: t("AuthAgentPolicyDetail-additional-benefits-label-2"),
                  data: (row) => `${row.beginDate}`,
                },
              ]
            : []),
          ...(isFlagEnabled("SHOW_EXPIRY_DATE_IN_ADDITIONAL_BENEFITS_SECTION")
            ? [
                ...(!isWholeLifePolicy(policy?.product?.type)
                  ? [
                      {
                        name: t(
                          "AuthAgentPolicyDetail-additional-benefits-label-3"
                        ),
                        data: (row) => `${row.expiryDate}`,
                      },
                    ]
                  : []),
              ]
            : []),
        ]}
        data={filterAndFormatRiderPolicies(
          policy?.supplementaryBenefitAndCharges
        )}
        className={{
          table: "ui-grid-table w-full",
        }}
      />
    );
  };

  return (
    <>
      <div className="ui-card">
        <div className="p-4 xl:pt-6 xl:px-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center">
            <h2 className="mb-0">
              <button
                id={uuid}
                className="mb-0 flex items-center group md:hidden"
                aria-controls={`${uuid}-panel`}
                aria-expanded="true"
                aria-label={t(
                  "AuthAgentPolicyDetail-card-component-aria-label"
                )}
              >
                <span className="inline-block p-1 rounded-full mr-1 text-ui-blue group-hover:text-white group-hover:bg-ui-blue">
                  <svg
                    className="ui-icon ui-plus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-plus"></use>
                  </svg>
                  <svg
                    className="ui-icon ui-minus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-minus"></use>
                  </svg>
                </span>
                {policy.badgeStatus && (
                  <span className="mr-1 flex items-center md:hidden">
                    {policy?.status && policy?.status !== "" && (
                      <Badge
                        iconOnly={true}
                        status={policy.badgeStatus}
                        statusText={getBadgeText(policy)}
                        srPrefix="Policy"
                      />
                    )}
                  </span>
                )}
                <span className="text-base font-body font-bold md:ui-no-badge-icon-inline">
                  {t("AuthAgentPolicyDetail-coverage-documents-title")}
                </span>
              </button>
              <span className="text-xl font-body font-bold hidden md:block">
                {t("AuthAgentPolicyDetail-coverage-documents-title")}
              </span>
            </h2>
            {policy.badgeStatus && (
              <div className="hidden md:block">
                {policy?.status && policy?.status !== "" && (
                  <Badge
                    status={policy.badgeStatus}
                    statusText={getBadgeText(policy)}
                    srPrefix="Policy"
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <hr className="ui-mobile-collapse" />
        <div
          id={`${uuid}-panel`}
          className="overflow-hidden h-20 p-4 md:h-auto xl:p-6"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-6">
            {config.ENABLE_SHOW_COMPANY_NAME_ON_ANNUITY_POLICIES === "true" && (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("AuthAgentPolicyDetail-coverage-documents-label-1")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {getCompanyNameByBlockCode(policy?.blockCode)}
                </div>
              </div>
            )}

            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("AuthAgentPolicyDetail-coverage-documentslabel-7")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {policy.oldPolicy}
              </div>
            </div>
            {!isMITPolicy(policy) && (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {" "}
                  {t("AuthAgentPolicyDetail-coverage-documents-label-8")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {stringFormattingToCurrency(
                    policy?.cashValue?.currentFaceAmount
                  )}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("AuthAgentPolicyDetail-coverage-documents-label-4")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {policy.productName}
              </div>
            </div>
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {" "}
                {t("AuthAgentPolicyDetail-coverage-documents-label-5")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {moment(policy?.startDate).format("MM/DD/YYYY")}
              </div>
            </div>
            {renderLapseDate(policy)}
            {(isWholeLifePolicy(policy?.product?.type) ||
              isOldAlicUlPolicy(policy)) && (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("AuthAgentPolicyDetail-coverage-documents-label-10")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {moment(policy?.maturityDate).format("MM/DD/YYYY")}
                </div>
              </div>
            )}
            {isOldAlicUlPolicy(policy) && (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t(
                    "AuthAgentPolicyDetail-coverage-documents-death-benefit-option-label"
                  )}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {t(
                    "AuthAgentPolicyDetail-coverage-documents-death-benefit-option-value",
                    {
                      value: policy?.currentDeathBenefitOption,
                    }
                  )}
                </div>
              </div>
            )}
            {isFlagEnabled("ENABLE_RATE_CLASS") &&
              !isOldAlicUlPolicy(policy) &&
              policy?.smokeCodeDescription && (
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("AuthAgentPolicyDetail-coverage-documents-label-9")}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {convertToTitleCase(policy?.smokeCodeDescription)}
                  </div>
                </div>
              )}
          </div>

          {isFlagEnabled("ENABLE_ADDITIONAL_BENEFITS_SECTION") &&
            policy?.supplementaryBenefitAndCharges?.length > 0 &&
            filterAndFormatRiderPolicies(policy?.supplementaryBenefitAndCharges)
              ?.length > 0 && (
              <>
                <div className="font-semibold mt-6 mb-1">
                  {t("AuthAgentPolicyDetail-additional-benefits-title")}
                </div>
                {additionalBenefitsGrid(policy)}
              </>
            )}

          <EverlakeLoadingOverlay isLoading={loading}>
            {filteredDocuments?.some(
              (document) =>
                document?.document?.typeOfDocument?.toUpperCase() === "ANNSTMT"
            ) && (
              <>
                <div className="font-semibold mt-6 mb-1">
                  {t("AuthAgentPolicyDetail-policy-documents-title")}
                </div>
                <div className="ui-download-document">
                  {filteredDocuments?.map((document, index) => {
                    if (
                      document?.document?.typeOfDocument?.toUpperCase() ===
                      "ANNSTMT"
                    ) {
                      return (
                        <div
                          className="inline-block py-2 px-4 border border-ui-gray-light-medium rounded"
                          key={`document${index}`}
                        >
                          <div className="text-xs font-semibold text-theme-text-secondary whitespace-nowrap">
                            {t("AuthAgentPolicyDetail-annual-statement-title")}{" "}
                            {moment(document?.document?.dateOfDocument).year()}
                          </div>
                          <div>
                            <button
                              className="ui-link-icon font-bold text-xs"
                              onClick={() => {
                                if (document?.document?.name) {
                                  setLoading(true);
                                  dispatch(
                                    getImage({
                                      imageFileName:
                                        document?.document?.name ?? "",
                                      policyId: document?.policy?.contractID,
                                    })
                                  )
                                    .then((response) => {
                                      setLoading(false);
                                      let byteArray = base64ToArrayBuffer(
                                        response.payload.document.contentStream
                                          .type
                                      );
                                      let blob = new Blob([byteArray], {
                                        type: "application/pdf",
                                      });
                                      fileSaver.saveAs(
                                        blob,
                                        `${document?.document?.name}.pdf`
                                      );
                                    })
                                    .catch((error) => {
                                      console.log("Error", error.message);
                                      setName(document?.document?.name);
                                      setPdfDialogState(true);
                                      setLoading(false);
                                      setDesc(document?.document?.description);
                                      setPdfSource("");
                                    });
                                }
                              }}
                            >
                              <svg
                                className="ui-icon w-4 h-4"
                                focusable="false"
                                aria-hidden="true"
                                role="presentation"
                              >
                                <use href="#ui-icon-document-download"></use>
                              </svg>
                              {t("AuthAgentPolicyDetail-download-button")}
                            </button>
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              </>
            )}
          </EverlakeLoadingOverlay>
        </div>

        <CardDisclaimerBase
          disclaimer={
            <Trans
              i18nKey={
                "AuthAgentPolicyDetail-coverage-documents-annualstmt-disclaimer"
              }
              components={{
                button1: (
                  <button
                    onClick={() => setOpenChatWindow(!openChatWindow)}
                    className="ui-btn-hyperlink text-xs font-semibold"
                  ></button>
                ),
              }}
            ></Trans>
          }
        />
      </div>

      <PDFDialog
        title={`${name ?? ""} ${t("AuthAgentPolicyDetail-pdf-preview-text")}`}
        description={desc}
        pdfSource={pdfSource}
        dialogState={pdfDialogState}
        setDialogState={setPdfDialogState}
      />
    </>
  );
};

export default CoverageAndDocuments;
